import React, { useState, useEffect } from 'react'
import { useAdminApi } from '../contexts/AdminApiContext'
import './ConnectionStatus.css'

interface SystemInfo {
  environment: {
    name: string
    api_base_url: string
    consumer_url: string
    creator_url: string
  }
  database: {
    available: boolean
    type: string
    status: string
    binding_name?: string
    database_name?: string
    database_id?: string
    location?: string
    user_count?: number
    error?: string
  }
  storage: {
    available: boolean
    type: string
    status: string
    binding_name?: string
    bucket_name?: string
    location?: string
    object_count?: number
    error?: string
  }
  timestamp: string
}

const ConnectionStatus: React.FC = () => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { apiService, isReady } = useAdminApi()

  const fetchSystemInfo = async () => {
    if (!isReady) return
    
    try {
      setLoading(true)
      setError(null)
      const info = await apiService.getSystemInfo()
      setSystemInfo(info)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch system info')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isReady) {
      fetchSystemInfo()
    }
  }, [isReady])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return '#10b981'
      case 'error': return '#ef4444'
      default: return '#6b7280'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return '✅'
      case 'error': return '❌'
      default: return '❓'
    }
  }

  if (loading) {
    return (
      <div className="connection-status">
        <div className="connection-status-header">
          <h3>Connection Status</h3>
          <button onClick={fetchSystemInfo} disabled className="refresh-button">
            🔄
          </button>
        </div>
        <div className="connection-loading">Loading connection information...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="connection-status">
        <div className="connection-status-header">
          <h3>Connection Status</h3>
          <button onClick={fetchSystemInfo} className="refresh-button">
            🔄
          </button>
        </div>
        <div className="connection-error">
          ⚠️ Failed to load connection status: {error}
        </div>
      </div>
    )
  }

  return (
    <div className="connection-status">
      <div className="connection-status-header">
        <h3>Connection Status</h3>
        <button onClick={fetchSystemInfo} className="refresh-button">
          🔄
        </button>
      </div>

      {systemInfo && (
        <>
          {/* Environment Info */}
          <div className="connection-section">
            <h4 className="section-title">🌍 Environment</h4>
            <div className="connection-item">
              <span className="item-label">Environment:</span>
              <span className="item-value environment-badge">{systemInfo.environment.name}</span>
            </div>
            <div className="connection-item">
              <span className="item-label">API Base:</span>
              <span className="item-value">{systemInfo.environment.api_base_url}</span>
            </div>
          </div>

          {/* Database Connection */}
          <div className="connection-section">
            <h4 className="section-title">🗄️ Database (D1)</h4>
            <div className="connection-item">
              <span className="item-label">Status:</span>
              <span 
                className="item-value status-indicator"
                style={{ color: getStatusColor(systemInfo.database.status) }}
              >
                {getStatusIcon(systemInfo.database.status)} {systemInfo.database.status}
              </span>
            </div>
            <div className="connection-item">
              <span className="item-label">Type:</span>
              <span className="item-value">{systemInfo.database.type}</span>
            </div>
            {systemInfo.database.database_name && (
              <div className="connection-item">
                <span className="item-label">Database:</span>
                <span className="item-value">{systemInfo.database.database_name}</span>
              </div>
            )}
            {systemInfo.database.database_id && (
              <div className="connection-item">
                <span className="item-label">Database ID:</span>
                <span className="item-value">{systemInfo.database.database_id}</span>
              </div>
            )}
            {systemInfo.database.binding_name && (
              <div className="connection-item">
                <span className="item-label">Binding:</span>
                <span className="item-value">{systemInfo.database.binding_name}</span>
              </div>
            )}
            {systemInfo.database.location && (
              <div className="connection-item">
                <span className="item-label">Location:</span>
                <span className="item-value">{systemInfo.database.location}</span>
              </div>
            )}
            {systemInfo.database.status === 'connected' && systemInfo.database.user_count !== undefined && (
              <div className="connection-item">
                <span className="item-label">Users:</span>
                <span className="item-value">{systemInfo.database.user_count}</span>
              </div>
            )}
            {systemInfo.database.error && (
              <div className="connection-item">
                <span className="item-label">Error:</span>
                <span className="item-value error-text">{systemInfo.database.error}</span>
              </div>
            )}
          </div>

          {/* Storage Connection */}
          <div className="connection-section">
            <h4 className="section-title">📦 Storage (R2)</h4>
            <div className="connection-item">
              <span className="item-label">Status:</span>
              <span 
                className="item-value status-indicator"
                style={{ color: getStatusColor(systemInfo.storage.status) }}
              >
                {getStatusIcon(systemInfo.storage.status)} {systemInfo.storage.status}
              </span>
            </div>
            <div className="connection-item">
              <span className="item-label">Type:</span>
              <span className="item-value">{systemInfo.storage.type}</span>
            </div>
            {systemInfo.storage.bucket_name && (
              <div className="connection-item">
                <span className="item-label">Bucket:</span>
                <span className="item-value">{systemInfo.storage.bucket_name}</span>
              </div>
            )}
            {systemInfo.storage.binding_name && (
              <div className="connection-item">
                <span className="item-label">Binding:</span>
                <span className="item-value">{systemInfo.storage.binding_name}</span>
              </div>
            )}
            {systemInfo.storage.location && (
              <div className="connection-item">
                <span className="item-label">Location:</span>
                <span className="item-value">{systemInfo.storage.location}</span>
              </div>
            )}
            {systemInfo.storage.status === 'connected' && systemInfo.storage.object_count !== undefined && (
              <div className="connection-item">
                <span className="item-label">Objects:</span>
                <span className="item-value">{systemInfo.storage.object_count}+ files</span>
              </div>
            )}
            {systemInfo.storage.error && (
              <div className="connection-item">
                <span className="item-label">Error:</span>
                <span className="item-value error-text">{systemInfo.storage.error}</span>
              </div>
            )}
          </div>

          <div className="connection-footer">
            <span className="last-updated">
              Last updated: {new Date(systemInfo.timestamp).toLocaleString()}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

export default ConnectionStatus